import type { ValidationRule } from "../types";

// Letters (incl. common Latin-1 accented chars), spaces, hyphens and
// apostrophes only — allows names like "O'Brien" and "Anne-Marie", rejects
// digits/symbols. Avoids \p{L}/u-flag for older compile targets.
const NAME_CHARS = "A-Za-z\\u00C0-\\u024F";
const NAME_REGEX = new RegExp(`^[${NAME_CHARS}][${NAME_CHARS} '-]*$`);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PHONE_ALLOWED_CHARS = /^[0-9 ()+-]+$/;

export const isValidName = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 100 && NAME_REGEX.test(trimmed);
};

export const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(value.trim());

/** UK mobile: normalises +44/44 to 0, then expects 11 digits starting `07`. */
export const isValidUkPhone = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed || !PHONE_ALLOWED_CHARS.test(trimmed)) {
    return false;
  }
  let digits = trimmed.replace(/[^\d+]/g, "");
  if (digits.startsWith("+44")) {
    digits = `0${digits.slice(3)}`;
  } else if (digits.startsWith("44") && digits.length === 12) {
    digits = `0${digits.slice(2)}`;
  }
  return /^07\d{9}$/.test(digits);
};

const RULE_MESSAGES: Record<ValidationRule, string> = {
  name: "Please use letters only — no numbers or symbols.",
  email: "Please enter a valid email address.",
  phone_uk: "Please enter a valid UK mobile number starting 07.",
};

/**
 * Validates a text answer against a server-driven rule. Returns an error
 * message, or null when valid. Empty values are treated as valid here — the
 * caller enforces "required" separately.
 */
export const validateTextRule = (
  rule: ValidationRule | undefined,
  value: unknown
): string | null => {
  if (!rule) {
    return null;
  }
  const text = typeof value === "string" ? value : "";
  if (!text.trim()) {
    return null;
  }
  switch (rule) {
    case "name":
      return isValidName(text) ? null : RULE_MESSAGES.name;
    case "email":
      return isValidEmail(text) ? null : RULE_MESSAGES.email;
    case "phone_uk":
      return isValidUkPhone(text) ? null : RULE_MESSAGES.phone_uk;
    default:
      return null;
  }
};
